/**
 * Features list rendered with the shared `page-table` styling used by
 * Tasks V2 and Defects V2. Feature rows expand to reveal sub-feature rows.
 */
export default function FeaturesTable({
  features,
  loading,
  expanded,
  subFeatures,
  subLoading,
  onToggleExpand,
  onOpenFeature,
  onOpenSubFeature,
  onAddSubFeature,
  onDeleteFeature,
  onDeleteSubFeature,
}) {
  if (!loading && features.length === 0) {
    return (
      <div className="page-table-wrap">
        <div className="ftv2-empty">No features found</div>
      </div>
    );
  }

  return (
    <div className="page-table-wrap">
      <table className="page-table">
        <colgroup>
          <col style={{ width: 36 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 160 }} />
          <col />
          <col style={{ width: 200 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 64 }} />
        </colgroup>
        <thead>
          <tr>
            <th />
            <th>Code</th>
            <th>Product</th>
            <th>Name / Description</th>
            <th>Assets / Flags</th>
            <th>Version</th>
            <th>Links</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {loading && features.length === 0 && (
            <tr><td colSpan={8} className="ftv2-state-cell">Loading features…</td></tr>
          )}
          {features.map((feature) => {
            const isOpen = expanded.has(feature.id);
            const subs = subFeatures[feature.id];
            const loadingSubs = !!subLoading[feature.id];
            const assets = Array.isArray(feature.applicable_asset) ? feature.applicable_asset : [];

            return (
              <FeatureRowFragment key={feature.id}
                feature={feature}
                isOpen={isOpen}
                assets={assets}
                subs={subs}
                loadingSubs={loadingSubs}
                onToggleExpand={onToggleExpand}
                onOpenFeature={onOpenFeature}
                onOpenSubFeature={onOpenSubFeature}
                onAddSubFeature={onAddSubFeature}
                onDeleteFeature={onDeleteFeature}
                onDeleteSubFeature={onDeleteSubFeature}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// React doesn't allow fragments to carry keys + multiple children via
// implicit array — wrap each feature group in its own component so the
// parent map stays clean.
function FeatureRowFragment({
  feature,
  isOpen,
  assets,
  subs,
  loadingSubs,
  onToggleExpand,
  onOpenFeature,
  onOpenSubFeature,
  onAddSubFeature,
  onDeleteFeature,
  onDeleteSubFeature,
}) {
  return (
    <>
      <tr className="ftv2-row ftv2-row--feature" onClick={() => onOpenFeature(feature)}>
        <td onClick={(e) => { e.stopPropagation(); onToggleExpand(feature.id); }}>
          <button type="button" className={`ftv2-expand${isOpen ? " is-open" : ""}`} aria-label={isOpen ? "Collapse" : "Expand"}>
            {isOpen ? "▼" : "►"}
          </button>
        </td>
        <td>
          <span className="ftv2-code ftv2-code--feature">{feature.feature_code || "—"}</span>
        </td>
        <td>
          {feature.product_name
            ? <span className="ftv2-product-tag">{feature.product_name}</span>
            : <span className="ftv2-muted">—</span>}
        </td>
        <td>
          <div className="ftv2-name">
            <span className="n1">{feature.feature_name || "—"}</span>
            {feature.description && <span className="n2">{feature.description}</span>}
          </div>
        </td>
        <td>
          <div className="ftv2-chips">
            {assets.length === 0 && <span className="ftv2-muted">—</span>}
            {assets.map((a) => (
              <span key={a} className="ftv2-asset-chip">{a}</span>
            ))}
          </div>
        </td>
        <td>
          <span className="ftv2-version">{feature.latest_version || "—"}</span>
        </td>
        <td>
          <div className="ftv2-links">
            {feature.repository && (
              <a href={feature.repository} target="_blank" rel="noreferrer" className="ftv2-link" onClick={(e) => e.stopPropagation()}>Repo ↗</a>
            )}
            {feature.document_url && (
              <a href={feature.document_url} target="_blank" rel="noreferrer" className="ftv2-link" onClick={(e) => e.stopPropagation()}>Docs ↗</a>
            )}
            {!feature.repository && !feature.document_url && <span className="ftv2-muted">—</span>}
          </div>
        </td>
        <td className="ftv2-row-actions-cell">
          {onDeleteFeature && (
            <button
              type="button"
              className="ftv2-row-action ftv2-row-action--danger"
              title="Delete feature"
              onClick={(e) => { e.stopPropagation(); onDeleteFeature(feature); }}
            >
              ✕
            </button>
          )}
        </td>
      </tr>

      {isOpen && loadingSubs && (
        <tr className="ftv2-row ftv2-row--sub-state">
          <td colSpan={8} className="ftv2-state-cell">Loading sub-features…</td>
        </tr>
      )}

      {isOpen && !loadingSubs && (subs || []).length === 0 && (
        <tr className="ftv2-row ftv2-row--sub-state">
          <td colSpan={8} className="ftv2-state-cell">No sub-features yet.</td>
        </tr>
      )}

      {isOpen && !loadingSubs && (subs || []).map((sf) => (
        <tr
          key={sf.id}
          className="ftv2-row ftv2-row--sub"
          onClick={() => onOpenSubFeature(sf, feature.id)}
        >
          <td className="ftv2-indent"><span className="ftv2-indent-rule" /></td>
          <td>
            <span className="ftv2-code ftv2-code--sub">{sf.sub_feature_code || "—"}</span>
          </td>
          <td />
          <td>
            <div className="ftv2-name">
              <span className="n1 n1--sub">{sf.sub_feature_name || "—"}</span>
              {sf.description && <span className="n2">{sf.description}</span>}
            </div>
          </td>
          <td>
            <div className="ftv2-chips">
              {sf.ip_type      && <span className="ftv2-flag ftv2-flag--ip">{sf.ip_type}</span>}
              {sf.is_BOD       && <span className="ftv2-flag">BOD</span>}
              {sf.is_scheduled && <span className="ftv2-flag">SCHED</span>}
              {sf.is_EOD       && <span className="ftv2-flag">EOD</span>}
              {sf.is_internal  && <span className="ftv2-flag ftv2-flag--int">INT</span>}
            </div>
          </td>
          <td />
          <td />
          <td className="ftv2-row-actions-cell">
            {onDeleteSubFeature && (
              <button
                type="button"
                className="ftv2-row-action ftv2-row-action--danger"
                title="Delete sub-feature"
                onClick={(e) => { e.stopPropagation(); onDeleteSubFeature(sf, feature.id); }}
              >
                ✕
              </button>
            )}
          </td>
        </tr>
      ))}

      {isOpen && !loadingSubs && onAddSubFeature && (
        <tr className="ftv2-row ftv2-row--add">
          <td className="ftv2-indent"><span className="ftv2-indent-rule" /></td>
          <td colSpan={7}>
            <button
              type="button"
              className="ftv2-add-sub"
              onClick={(e) => { e.stopPropagation(); onAddSubFeature(feature.id); }}
            >
              <span className="plus">+</span> Add Sub-feature
            </button>
          </td>
        </tr>
      )}
    </>
  );
}
